import React, { useState, useEffect } from 'react';
import { useI18n } from '../../i18n';
import { auth } from '../../api/client';
import { Mail, Send, FileText, Target, Users, BarChart3, Settings, AlertTriangle, CheckCircle, Clock, ExternalLink } from 'lucide-react';

const PhishingPage = () => {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState('campaigns');
  const [userRole, setUserRole] = useState(null);
  const [isSuperuser, setIsSuperuser] = useState(false);

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const userData = await auth.getCurrentUser();
        setUserRole(userData?.role || '');
        setIsSuperuser(userData?.is_superuser || false);
      } catch (error) {
        console.error('Error fetching user role:', error);
      }
    };
    fetchUserRole();
  }, []);

  const isSuperAdmin = userRole === 'super_admin' || isSuperuser;

  const tabs = [
    {
      id: 'campaigns',
      label: t('phishing.campaignsTab', 'Campaigns'),
      icon: Send,
      component: CampaignsDashboard
    },
    {
      id: 'templates',
      label: t('phishing.templatesTab', 'Templates'),
      icon: FileText,
      component: TemplatesSection
    },
    {
      id: 'targets',
      label: t('phishing.targetsTab', 'Target Groups'),
      icon: Users,
      component: TargetGroupsSection
    },
    {
      id: 'analytics',
      label: t('phishing.analyticsTab', 'Analytics'),
      icon: BarChart3,
      component: AnalyticsSection
    }
  ];

  // Add admin tab for superadmins
  if (isSuperAdmin) {
    tabs.push({
      id: 'settings',
      label: t('phishing.settingsTab', 'Settings'),
      icon: Settings,
      component: SettingsSection
    });
  }

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component || CampaignsDashboard;

  return (
    <div className="flex-1 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-xl bg-orange-500/20 border border-orange-500/30">
              <Mail className="w-8 h-8 text-orange-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">
                {t('phishing.title', 'Phishing Simulation')}
              </h1>
              <p className="text-gray-400">
                {t('phishing.subtitle', 'Test employee security awareness with simulated phishing campaigns')}
              </p>
            </div>
          </div>

          {/* MITRE ATT&CK Reference */}
          <div className="mt-4 flex items-center gap-2 text-sm">
            <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded border border-red-500/30">
              MITRE ATT&CK
            </span>
            <span className="text-gray-400">T1566 - Phishing</span>
            <a
              href="https://attack.mitre.org/techniques/T1566/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-green hover:text-brand-green/80 flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" />
              {t('phishing.reference', 'Référence')}
            </a>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="flex gap-2 p-1 bg-gray-900/50 rounded-lg border border-gray-700/50 w-fit flex-wrap">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    activeTab === tab.id
                      ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                      : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Active Tab Content */}
        <ActiveComponent />
      </div>
    </div>
  );
};

// Campaigns Dashboard Component
const CampaignsDashboard = () => {
  const { t } = useI18n();

  const mockCampaigns = [
    {
      id: 1,
      name: 'Test de sensibilisation Q1',
      status: 'completed',
      sent: 150,
      opened: 89,
      clicked: 23,
      reported: 45,
      created: '2025-01-15'
    },
    {
      id: 2,
      name: 'Évaluation Département IT',
      status: 'active',
      sent: 45,
      opened: 32,
      clicked: 5,
      reported: 12,
      created: '2025-01-20'
    },
    {
      id: 3,
      name: 'Test de l\'équipe de direction',
      status: 'scheduled',
      sent: 0,
      opened: 0,
      clicked: 0,
      reported: 0,
      created: '2025-01-28'
    }
  ];

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-500/20 text-green-400 border-green-500/30',
      active: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      scheduled: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
      draft: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    const icons = {
      completed: CheckCircle,
      active: Send,
      scheduled: Clock,
      cancelled: AlertTriangle,
      draft: FileText
    };
    const statusLabels = {
      completed: t('phishing.statusCompleted', 'Terminé'),
      active: t('phishing.statusActive', 'Active'),
      scheduled: t('phishing.statusScheduled', 'Planifiée'),
      cancelled: t('phishing.statusCancelled', 'Annulée'),
      draft: t('phishing.statusDraft', 'Brouillon')
    };
    const Icon = icons[status] || FileText;
    return (
      <span className={`px-2 py-1 rounded border text-xs flex items-center gap-1 ${styles[status] || styles.draft}`}>
        <Icon className="w-3 h-3" />
        {statusLabels[status] || status}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Send className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">3</p>
              <p className="text-sm text-gray-400">{t('phishing.totalCampaigns', 'Total Campaigns')}</p>
            </div>
          </div>
        </div>
        <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/20">
              <Mail className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">195</p>
              <p className="text-sm text-gray-400">{t('phishing.emailsSent', 'Emails Sent')}</p>
            </div>
          </div>
        </div>
        <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-500/20">
              <Target className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">14.4%</p>
              <p className="text-sm text-gray-400">{t('phishing.clickRate', 'Click Rate')}</p>
            </div>
          </div>
        </div>
        <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <AlertTriangle className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">29.2%</p>
              <p className="text-sm text-gray-400">{t('phishing.reportRate', 'Report Rate')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Campaign Actions */}
      <div className="flex justify-between items-center bg-gray-900/30 p-4 rounded-xl border border-gray-700/30">
        <h2 className="text-xl font-semibold text-white">{t('phishing.recentCampaigns', 'Recent Campaigns')}</h2>
        <button className="px-4 py-2 bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 rounded-lg border border-orange-500/30 flex items-center gap-2 transition-colors">
          <Send className="w-4 h-4" />
          {t('phishing.newCampaign', 'New Campaign')}
        </button>
      </div>

      {/* Campaigns Table */}
      <div className="bg-gray-900/50 rounded-xl border border-gray-700/50 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700/50">
              <th className="text-left p-4 text-gray-400 font-medium">{t('phishing.campaignName', 'Campaign Name')}</th>
              <th className="text-left p-4 text-gray-400 font-medium">{t('phishing.status', 'Status')}</th>
              <th className="text-center p-4 text-gray-400 font-medium">{t('phishing.sent', 'Sent')}</th>
              <th className="text-center p-4 text-gray-400 font-medium">{t('phishing.opened', 'Opened')}</th>
              <th className="text-center p-4 text-gray-400 font-medium">{t('phishing.clicked', 'Clicked')}</th>
              <th className="text-center p-4 text-gray-400 font-medium">{t('phishing.reported', 'Reported')}</th>
              <th className="text-right p-4 text-gray-400 font-medium">{t('phishing.actions', 'Actions')}</th>
            </tr>
          </thead>
          <tbody>
            {mockCampaigns.map((campaign) => (
              <tr key={campaign.id} className="border-b border-gray-700/30 hover:bg-gray-800/30 transition-colors">
                <td className="p-4">
                  <p className="text-white font-medium">{campaign.name}</p>
                  <p className="text-xs text-gray-500">{campaign.created}</p>
                </td>
                <td className="p-4">{getStatusBadge(campaign.status)}</td>
                <td className="p-4 text-center text-white">{campaign.sent}</td>
                <td className="p-4 text-center text-blue-400">{campaign.opened}</td>
                <td className="p-4 text-center text-orange-400">{campaign.clicked}</td>
                <td className="p-4 text-center text-green-400">{campaign.reported}</td>
                <td className="p-4 text-right">
                  <button className="text-brand-green hover:text-brand-green/80 text-sm">
                    {t('phishing.viewDetails', 'View Details')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Templates Section Component
const TemplatesSection = () => {
  const { t } = useI18n();

  const mockTemplates = [
    { id: 1, name: 'Demande de réinitialisation', category: 'credential_harvest', difficulty: 'moyen' },
    { id: 2, name: 'Ticket support IT', category: 'link_click', difficulty: 'facile' },
    { id: 3, name: 'Pièce jointe facture', category: 'attachment', difficulty: 'difficile' },
    { id: 4, name: 'Invitation réunion', category: 'link_click', difficulty: 'facile' },
  ];

  const getCategoryLabel = (category) => {
    const categoryLabels = {
      credential_harvest: t('phishing.categoryCredentialHarvest', 'Collecte d\'identifiants'),
      link_click: t('phishing.categoryLinkClick', 'Clic sur lien'),
      attachment: t('phishing.categoryAttachment', 'Pièce jointe')
    };
    return categoryLabels[category] || category;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-gray-900/30 p-4 rounded-xl border border-gray-700/30">
        <h2 className="text-xl font-semibold text-white">{t('phishing.emailTemplates', 'Email Templates')}</h2>
        <button className="px-4 py-2 bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 rounded-lg border border-orange-500/30 flex items-center gap-2 transition-colors">
          <FileText className="w-4 h-4" />
          {t('phishing.createTemplate', 'Create Template')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {mockTemplates.map((template) => (
          <div key={template.id} className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/50 hover:border-orange-500/30 transition-colors cursor-pointer">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 rounded-lg bg-orange-500/20">
                <Mail className="w-5 h-5 text-orange-400" />
              </div>
              <span className={`px-2 py-1 rounded text-xs ${
                template.difficulty === 'facile' ? 'bg-green-500/20 text-green-400' :
                template.difficulty === 'moyen' ? 'bg-yellow-500/20 text-yellow-400' :
                'bg-red-500/20 text-red-400'
              }`}>
                {template.difficulty}
              </span>
            </div>
            <h3 className="text-white font-medium mb-1">{template.name}</h3>
            <p className="text-sm text-gray-400">{getCategoryLabel(template.category)}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Target Groups Section Component
const TargetGroupsSection = () => {
  const { t } = useI18n();

  const mockGroups = [
    { id: 1, name: 'Tous les employés', count: 150, lastUsed: '2025-01-15' },
    { id: 2, name: 'Département IT', count: 25, lastUsed: '2025-01-20' },
    { id: 3, name: 'Équipe de direction', count: 8, lastUsed: '2025-01-10' },
    { id: 4, name: 'Équipe commerciale', count: 45, lastUsed: '2025-01-05' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-gray-900/30 p-4 rounded-xl border border-gray-700/30">
        <h2 className="text-xl font-semibold text-white">{t('phishing.targetGroups', 'Target Groups')}</h2>
        <button className="px-4 py-2 bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 rounded-lg border border-orange-500/30 flex items-center gap-2 transition-colors">
          <Users className="w-4 h-4" />
          {t('phishing.createGroup', 'Create Group')}
        </button>
      </div>

      <div className="bg-gray-900/50 rounded-xl border border-gray-700/50 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700/50">
              <th className="text-left p-4 text-gray-400 font-medium">{t('phishing.groupName', 'Group Name')}</th>
              <th className="text-center p-4 text-gray-400 font-medium">{t('phishing.members', 'Members')}</th>
              <th className="text-center p-4 text-gray-400 font-medium">{t('phishing.lastUsed', 'Last Used')}</th>
              <th className="text-right p-4 text-gray-400 font-medium">{t('phishing.actions', 'Actions')}</th>
            </tr>
          </thead>
          <tbody>
            {mockGroups.map((group) => (
              <tr key={group.id} className="border-b border-gray-700/30 hover:bg-gray-800/30 transition-colors">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-500/20">
                      <Users className="w-4 h-4 text-blue-400" />
                    </div>
                    <span className="text-white font-medium">{group.name}</span>
                  </div>
                </td>
                <td className="p-4 text-center text-white">{group.count}</td>
                <td className="p-4 text-center text-gray-400">{group.lastUsed}</td>
                <td className="p-4 text-right">
                  <button className="text-brand-green hover:text-brand-green/80 text-sm mr-3">
                    {t('phishing.edit', 'Edit')}
                  </button>
                  <button className="text-red-400 hover:text-red-300 text-sm">
                    {t('phishing.delete', 'Delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Analytics Section Component
const AnalyticsSection = () => {
  const { t } = useI18n();

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-white">{t('phishing.analyticsOverview', 'Analytics Overview')}</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Click Rate Over Time */}
        <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700/50">
          <h3 className="text-white font-medium mb-4">{t('phishing.clickRateTrend', 'Click Rate Trend')}</h3>
          <div className="h-48 flex items-center justify-center text-gray-500">
            <BarChart3 className="w-12 h-12 opacity-30" />
          </div>
          <p className="text-sm text-gray-400 text-center mt-2">{t('phishing.chartPlaceholder', 'Chart will display here')}</p>
        </div>

        {/* Report Rate Over Time */}
        <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700/50">
          <h3 className="text-white font-medium mb-4">{t('phishing.reportRateTrend', 'Report Rate Trend')}</h3>
          <div className="h-48 flex items-center justify-center text-gray-500">
            <BarChart3 className="w-12 h-12 opacity-30" />
          </div>
          <p className="text-sm text-gray-400 text-center mt-2">{t('phishing.chartPlaceholder', 'Chart will display here')}</p>
        </div>

        {/* Department Comparison */}
        <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700/50 md:col-span-2">
          <h3 className="text-white font-medium mb-4">{t('phishing.departmentComparison', 'Department Comparison')}</h3>
          <div className="space-y-3">
            {[
              { name: 'Département IT', clickRate: 8, reportRate: 65 },
              { name: 'Équipe commerciale', clickRate: 22, reportRate: 15 },
              { name: 'Marketing', clickRate: 18, reportRate: 28 },
              { name: 'Direction', clickRate: 12, reportRate: 45 },
            ].map((dept) => (
              <div key={dept.name} className="flex items-center gap-4">
                <span className="text-gray-400 w-32 text-sm">{dept.name}</span>
                <div className="flex-1 flex gap-2">
                  <div className="flex-1 h-4 bg-gray-800 rounded overflow-hidden">
                    <div
                      className="h-full bg-orange-500/60"
                      style={{ width: `${dept.clickRate}%` }}
                      title={`Click Rate: ${dept.clickRate}%`}
                    />
                  </div>
                  <div className="flex-1 h-4 bg-gray-800 rounded overflow-hidden">
                    <div
                      className="h-full bg-green-500/60"
                      style={{ width: `${dept.reportRate}%` }}
                      title={`Report Rate: ${dept.reportRate}%`}
                    />
                  </div>
                </div>
                <div className="w-24 text-right">
                  <span className="text-orange-400 text-xs">{dept.clickRate}%</span>
                  <span className="text-gray-500 mx-1">/</span>
                  <span className="text-green-400 text-xs">{dept.reportRate}%</span>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-6 mt-4 text-xs text-gray-400">
            <span className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-orange-500/60" />
              {t('phishing.clickRate', 'Click Rate')}
            </span>
            <span className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-green-500/60" />
              {t('phishing.reportRate', 'Report Rate')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Settings Section Component (Admin only)
const SettingsSection = () => {
  const { t } = useI18n();

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-white">{t('phishing.settings', 'Phishing Settings')}</h2>

      <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700/50 space-y-6">
        {/* SMTP Configuration */}
        <div>
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5 text-orange-400" />
            {t('phishing.smtpConfig', 'SMTP Configuration')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">{t('phishing.smtpServer', 'SMTP Server')}</label>
              <input
                type="text"
                className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-500/50"
                placeholder="smtp.exemple.com"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">{t('phishing.smtpPort', 'SMTP Port')}</label>
              <input
                type="number"
                className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-500/50"
                placeholder="587"
              />
            </div>
          </div>
        </div>

        {/* Tracking Settings */}
        <div className="pt-4 border-t border-gray-700/50">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-orange-400" />
            {t('phishing.trackingSettings', 'Tracking Settings')}
          </h3>
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" className="w-4 h-4 rounded border-gray-600 text-orange-500 focus:ring-orange-500 bg-gray-800" defaultChecked />
              <span className="text-gray-300">{t('phishing.trackOpens', 'Track email opens')}</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" className="w-4 h-4 rounded border-gray-600 text-orange-500 focus:ring-orange-500 bg-gray-800" defaultChecked />
              <span className="text-gray-300">{t('phishing.trackClicks', 'Track link clicks')}</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" className="w-4 h-4 rounded border-gray-600 text-orange-500 focus:ring-orange-500 bg-gray-800" defaultChecked />
              <span className="text-gray-300">{t('phishing.trackSubmissions', 'Track credential submissions')}</span>
            </label>
          </div>
        </div>

        {/* Save Button */}
        <div className="pt-4 border-t border-gray-700/50 flex justify-end">
          <button className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors">
            {t('phishing.saveSettings', 'Save Settings')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhishingPage;
